import { Box, styled } from '@mui/material';
import classNames from 'classnames';

import { EFlightCategory } from '#shared';

import { type TafForecast } from '../lib';

const PREFIX = 'TSMED-TafForecast';

export const tafForecastClasses = {
  time: `${PREFIX}-time`,
  flightCategory: `${PREFIX}-flightCategory`,
  flightCategoryIcon: `${PREFIX}-flightCategoryIcon`,
  flightCategoryLabel: `${PREFIX}-flightCategoryLabel`,
  fields: `${PREFIX}-fields`,
  field: `${PREFIX}-field`,
  fieldLabel: `${PREFIX}-fieldLabel`,
  fieldValue: `${PREFIX}-fieldValue`,
};

const Root = styled(Box)(({ theme: { spacing, palette, utils } }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: spacing(1),
  [`.flight-category-${EFlightCategory.IFR}`]: {
    color: palette.flightCategory.IFR,
    background: `${palette.flightCategory.IFR}1A`,
  },
  [`.flight-category-${EFlightCategory.LIFR}`]: {
    color: palette.flightCategory.LIFR,
    background: `${palette.flightCategory.LIFR}1A`,
  },
  [`.flight-category-${EFlightCategory.MVFR}`]: {
    color: palette.flightCategory.MVFR,
    background: `${palette.flightCategory.MVFR}1A`,
  },
  [`.flight-category-${EFlightCategory.VFR}`]: {
    color: palette.flightCategory.VFR,
    background: `${palette.flightCategory.VFR}1A`,
  },
  [`.${tafForecastClasses.time}`]: {
    color: palette.grey[700],
  },
  [`.${tafForecastClasses.flightCategory}`]: {
    display: 'flex',
    gap: spacing(1),
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: utils.clamp(12, 20),

    [`.${tafForecastClasses.flightCategoryIcon}`]: {
      color: 'inherit',
      background: 'currentColor',
      height: utils.clamp(14, 20),
      width: utils.clamp(14, 20),
      borderRadius: '50%',
    },
    [`.${tafForecastClasses.flightCategoryLabel}`]: {},
  },
  [`.${tafForecastClasses.fields}`]: {
    display: 'flex',
    gap: spacing(1),
    flexDirection: 'column',
    alignItems: 'stretch',
  },
  [`.${tafForecastClasses.field}`]: {
    display: 'flex',
    gap: spacing(2.5),
    flexDirection: 'row',
    alignItems: 'center',
    fontSize: utils.clamp(12, 20),
  },
  [`.${tafForecastClasses.fieldLabel}`]: {
    width: 180,
    textAlign: 'right',
    flexShrink: 0,
  },
  [`.${tafForecastClasses.fieldValue}`]: {
    color: 'white',
    flexGrow: 1,
  },
}));

export const Forecast = ({
  forecast: fc,
}: {
  forecast: TafForecast;
  timezone: string;
}) => (
  <Root>
    <Box className={tafForecastClasses.time}>
      {fc.validFrom?.format('HH:mm z')} ({fc.changeIndicator})
    </Box>
    <Box
      className={classNames(tafForecastClasses.flightCategory, {
        [`flight-category-${fc.flightCategory}`]: fc.flightCategory,
      })}
    >
      <Box className={tafForecastClasses.flightCategoryIcon} />
      <Box className={tafForecastClasses.flightCategoryLabel}>
        {fc.flightCategory}
      </Box>
    </Box>
    <Box className={tafForecastClasses.fields}>
      {fc.windFormatted ? (
        <Box className={tafForecastClasses.field}>
          <Box className={tafForecastClasses.fieldLabel}>Winds</Box>
          <Box className={tafForecastClasses.fieldValue}>
            {fc.windFormatted}
          </Box>
        </Box>
      ) : null}
      {fc.visibilityFormatted ? (
        <Box className={tafForecastClasses.field}>
          <Box className={tafForecastClasses.fieldLabel}>Visibility</Box>
          <Box className={tafForecastClasses.fieldValue}>
            {fc.visibilityFormatted}
          </Box>
        </Box>
      ) : null}
      {fc.weather?.trim() ? (
        <Box className={tafForecastClasses.field}>
          <Box className={tafForecastClasses.fieldLabel}>Weather</Box>
          <Box className={tafForecastClasses.fieldValue}>
            {fc.weather.trim()}
          </Box>
        </Box>
      ) : null}
      {fc.clouds ? (
        <Box className={tafForecastClasses.field}>
          <Box className={tafForecastClasses.fieldLabel}>Clouds</Box>
          <Box className={tafForecastClasses.fieldValue}>
            {((text: string) => {
              if (!text) {
                return '';
              }
              return [...text][0].toUpperCase() + [...text].slice(1).join('');
            })(fc.clouds.map(({ formatted }) => formatted).join(', '))}
            .
          </Box>
        </Box>
      ) : null}
    </Box>
  </Root>
);
